import { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import classNames from 'classnames';

import { useAppContext } from 'services/context';
import { useLocalStorage } from 'services/hooks';

import { FILE_TYPES, ROUTES } from 'constants/index.js';

import './style.css';

const Details = ({
	title,
	detailsTitle,
	chapter,
	quiz,
	chapterId,
	progress,
	recordId,
	materials,
	materialGroups,
	disabled,
	isOccured,
	isTEF,
	isFinal,
	middlewareCallback,
	displayPopupCoursesBeforeFinalExam,
	pdfCallBack,
	courseChapters,
	renderDetails,
	isUnit,
}) => {
	////isOccured used to give an indication if the user has passed or is in this chapter
	const navigate = useNavigate();
	const { setVideo, Api } = useAppContext();

	const detailsRef = useRef();
	const wrapperRef = useRef();
	const groupWrapperRef = useRef();

	const [hasChildNodes, setHasChildNodes] = useState(false);
	const [hasChildGroupNodes, setHasChildGroupNodes] = useState(false);
	const [visitCourseLinks, setVisitCourseLinks] = useLocalStorage(`visitCourseLinks${recordId}`, {});

	const isDetailsSummaryCompleted = useMemo(() => {
		//ESL General courses do not include a quiz for each chapter, only a final exam.
		return progress >= 2 || (!quiz?.testURL && !isFinal && progress >= 1) || (typeof isOccured != 'undefined' && isOccured);
	}, [progress, isOccured, quiz]);

	const summaryClasses = classNames('Details__summary', {
		'Details__summary--completed': isDetailsSummaryCompleted,
		disabled: disabled,
		hasChilds: hasChildNodes,
		unit: isUnit,
	});
	const detailsClasses = classNames('Details', {
		hasChilds: hasChildNodes,
		hasGroup: hasChildGroupNodes,
		unit: isUnit,
	});
	const groupDetailsClasses = classNames('GroupDetails', {
		hasChilds: hasChildNodes,
		isGroup: hasChildGroupNodes,
	});

	const chlidernClasses = classNames('Details__children', { 'Details__children--completed': progress >= 1 });
	const quizClasses = classNames('Details__children', { 'Details__children--completed': progress >= 2 }, { disabled: quiz?.isDisabled });

	//Returns the client to the chapter he was in before and highlights the material he clicked on.
	useEffect(() => {
		const { chapterGroupId, fileOrder, fileTypeID } = visitCourseLinks;
		if (!materials || !chapterGroupId) return;

		if (chapterId !== visitCourseLinks.chapterId) return;

		openElement(detailsRef.current);
		scrollIntoElement(detailsRef.current, fileTypeID);

		const children = Array.from(wrapperRef.current.children);

		//Checks what material he clicked on within the chapter (for courses that do not have groups in their structure).
		if (!chapterGroupId) {
			const fileOrderIdxIelts = children.findIndex(c => c.id == fileOrder);
			if (fileOrderIdxIelts === -1) return;
			scrollIntoElement(children[fileOrderIdxIelts], fileTypeID);
			return;
		}

		//Checks what group he clicked on within the chapter (for courses that have groups in their structure).
		const idx = children.findIndex(c => c.id == chapterGroupId);
		if (idx === -1) return;

		const groupChildren = children[idx].children;
		openElement(children[idx]);

		//Checks what material he clicked on within the chapter group.
		const fileOrderIdx = Array.from(groupChildren).findIndex(c => c.id == fileOrder);
		if (fileOrderIdx === -1) return;

		scrollIntoElement(groupChildren[fileOrderIdx], fileTypeID);
	}, [visitCourseLinks]);

	const openElement = el => {
		el.setAttribute('open', true);
	};

	const scrollIntoElement = (el, fileTypeID) => {
		if (fileTypeID && fileTypeID === FILE_TYPES.PDF) return;
		el.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		if (!wrapperRef.current) return;
		setHasChildNodes(wrapperRef.current.hasChildNodes());

		if (groupWrapperRef?.current?.hasChildNodes()) setHasChildGroupNodes(groupWrapperRef?.current?.hasChildNodes());
	}, [chapter, quiz]);

	const _handleNavigateClick = (url, type) => {
		if (!url) return;
		//navigate to quiz only if the quiz aren't locked
		if (quiz?.isDisabled && type === 'quiz') return;
		// if its IELTS Final Exam will stop the function and then redirect
		// from _handleIELTSTestNavigation on Course component
		if (isFinal && displayPopupCoursesBeforeFinalExam && typeof middlewareCallback === 'function') {
			middlewareCallback();
			return;
		}

		try {
			const path = generatePath(url, {
				recordId,
				chapterId,
			});

			//Update student status.
			if (isFinal && isTEF) Api.UpdateTestStatus(recordId);

			navigate(path);
		} catch (error) {
			console.warn(`Enable to navigate on chapter ${chapterId} ${title}`);
		}
	};

	const _handleMaterialClick = async material => {
		const { fileURL, fileDisplayName, fileTypeSurFix, fileTypeID, fileOrder, chapterGroupId } = material;

		if (visitCourseLinks) setVisitCourseLinks({ chapterId, chapterGroupId, fileOrder, fileTypeID });

		if (material.fileTypeID === FILE_TYPES.VIDEO || material.fileTypeID === FILE_TYPES.AUDIO) {
			setVideo({
				src: fileURL,
				title: fileDisplayName,
				isVideo: material.fileTypeID === FILE_TYPES.VIDEO,
				isAudio: material.fileTypeID === FILE_TYPES.AUDIO,
			});
			navigate(ROUTES.VIDEO_PLAYER_PAGE);
			return;
		}
		if (material.fileTypeID === FILE_TYPES.PDF && pdfCallBack && typeof pdfCallBack === 'function') {
			pdfCallBack(fileURL);
			return;
		}
		const link = document.createElement('a');
		link.href = fileURL; //create a blobURI pointing to our Blob
		link.download = `${fileDisplayName}.${fileTypeSurFix}`; // some browser needs the anchor to be in the doc
		link.click();
	};

	const renderMaterial = (material, idx) => {
		const { fileDisplayName, fileTypeName } = material;
		const isVisited = visitCourseLinks.chapterId === chapterId && visitCourseLinks.fileOrder === material.fileOrder;
		const materialClasses = classNames('Details__children', {
			[fileTypeName]: fileTypeName,
			disabled: disabled,
			lastVisitedLink: isVisited,
		});
		return (
			<div className={materialClasses} onClick={() => _handleMaterialClick(material)} key={idx} id={material?.fileOrder}>
				{fileDisplayName}
			</div>
		);
	};
	const renderMaterialGroup = group => {
		return group.materials.map(g => {
			return renderMaterial(g);
		});
	};

	const _handlePreventDisabledDetailsOpening = e => {
		if (disabled) e.preventDefault();
	};

	return (
		<details title={detailsTitle} className={detailsClasses} onClick={_handlePreventDisabledDetailsOpening} ref={detailsRef}>
			<summary className={summaryClasses}>{<div>{title}</div>}</summary>
			<div className={`children_wrapper ${isUnit ? 'unit' : ''}`} ref={wrapperRef}>
				{chapter?.chapterURL && chapter?.chapterDescription && (
					<div className={chlidernClasses} onClick={() => _handleNavigateClick(chapter.chapterURL)}>
						{chapter && chapter.chapterDescription}
					</div>
				)}
				{materialGroups?.length > 0 &&
					materialGroups.map((g, idx) => {
						// const materialClasses = classNames('Details__children', { [fileTypeName]: g.fileTypeName, disabled: disabled });
						return (
							<details
								className={groupDetailsClasses}
								onClick={_handlePreventDisabledDetailsOpening}
								ref={groupWrapperRef}
								key={idx}
								id={g.chapterGroupId}
							>
								<summary className={summaryClasses}>{g.chapterGroupName}</summary>
								{renderMaterialGroup(g, idx)}
							</details>
						);
					})}
				{quiz?.testURL && (
					<div className={quizClasses} onClick={() => _handleNavigateClick(quiz.testURL, 'quiz')}>
						{quiz && quiz.testHeaderText}
					</div>
				)}
				{materials?.length > 0 && materials.map(renderMaterial)}
				{courseChapters?.length > 0 && courseChapters?.sort((a, b) => a.chapterOrderInCourse > b.chapterOrderInCourse).map(renderDetails)}
			</div>
		</details>
	);
};

export default Details;
